import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import {
  makeSkillWebCompanyRedirectUrl,
  makeSkillWebRequestsRedirectUrl
} from '@/main/factories/http'
import PathConstants from '@/main/routes/path-contants'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import useRouteChange from '@/presentation/hooks/navigate/use-route-change'
import theme from '@assets/styles/theme'
import { Box, Container, Tabs } from '@mui/material'
import { Analytics } from '@/presentation/components/ui/header/analytics'
import { TabApp } from '@presentation/components/ui/navigation/tab/tab-app'
import { TabMenuApp } from '@presentation/components/ui/navigation/tab/tab-menu-app'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { SyntheticEvent, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { i18nTranslationHelper } from '@/i18n'

export function DesktopNavMenuCompanyManager() {
  const {
    COMPANY_MANAGER_DASHBOARD,
    COMPANY_MANAGER_EDIT_USER_PROFILE,
    COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE,
    COMPANY_MANAGER_DEPENDENT_LIST,
    COMPANY_MANAGER_HOLDER_SEARCH,
    COMPANY_MANAGER_DEPENDENT_REGISTRATION,
    COMPANY_MANAGER_DEPENDENT_ENROLLMENT,
    COMPANY_MANAGER_REQUESTS_CONFIGURATION,
    COMPANY_MANAGER_UPGRADE_ENROLLMENT
  } = PathConstants.Routes
  const { MONOLITH_MANAGE_CUSTOMER } = PathConstants.ExternalRoutes

  const [account] = useAccount() as ValidAccountState
  const location = useLocation()
  const [tabSelectedValue, setTabSelectedValue] = useState<string | false>(false)

  const handleTabClick = (_: SyntheticEvent, newValue: string | false) => {
    Analytics.trackNavMenu(newValue)
  }

  const dashboardAllowed: boolean =
    FeatureFlag.Key.COMPANIES_ON_COMPANY_MANAGER_DASHBOARDS.getValue(useLDClient())

  const dependentAllowed: boolean =
    FeatureFlag.Key.COMPANIES_ON_DEPENDENT_ROLLOUT.getValue(useLDClient())

  const enabledUpgradeV4Flag: boolean = FeatureFlag.Key.ENABLED_UPGRADE_V4.isEnabled(useLDClient())

  const t = i18nTranslationHelper.getTComponents

  const params = useParams()

  const identifyLocation = (): string | false => {
    const routes = [
      COMPANY_MANAGER_DASHBOARD,
      { ...COMPANY_MANAGER_EDIT_USER_PROFILE, name: MONOLITH_MANAGE_CUSTOMER.name },
      COMPANY_MANAGER_DEPENDENT_LIST,
      { ...COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE, name: COMPANY_MANAGER_DEPENDENT_LIST.name },
      { ...COMPANY_MANAGER_HOLDER_SEARCH, name: COMPANY_MANAGER_DEPENDENT_LIST.name },
      { ...COMPANY_MANAGER_DEPENDENT_REGISTRATION, name: COMPANY_MANAGER_DEPENDENT_LIST.name }
    ]
    return (
      routes.find(
        route =>
          (route.computeValue && params ? route.computeValue(params) : route.value) ===
          location.pathname
      )?.name ?? false
    )
  }

  useRouteChange(() => {
    const locationValue = identifyLocation()
    setTabSelectedValue(locationValue)
  })

  const tabsSx = {
    minHeight: theme.spacing(8),
    height: theme.spacing(8)
  }

  return (
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Tabs
          value={tabSelectedValue}
          onChange={handleTabClick}
          variant="standard"
          allowScrollButtonsMobile
          sx={{
            height: theme.spacing('auto')
          }}
        >
          {dashboardAllowed && (
            <TabApp
              component={Link}
              to={COMPANY_MANAGER_DASHBOARD.value}
              label={COMPANY_MANAGER_DASHBOARD.title}
              value={COMPANY_MANAGER_DASHBOARD.name}
              selectedValue={tabSelectedValue}
            />
          )}

          <TabApp
            component={Link}
            to={MONOLITH_MANAGE_CUSTOMER.value}
            label={MONOLITH_MANAGE_CUSTOMER.title}
            value={MONOLITH_MANAGE_CUSTOMER.name}
            selectedValue={tabSelectedValue}
            sx={tabsSx}
          />

          {dependentAllowed && (
            <TabApp
              component={Link}
              to={COMPANY_MANAGER_DEPENDENT_LIST.value}
              label={COMPANY_MANAGER_DEPENDENT_LIST.title}
              value={COMPANY_MANAGER_DEPENDENT_LIST.name}
              selectedValue={tabSelectedValue}
              sx={tabsSx}
            />
          )}

          <TabApp
            component={Link}
            to={makeSkillWebRequestsRedirectUrl()}
            label={t('navUserMenu.orderTitle')}
            value="monolith_authorizations"
            selectedValue={tabSelectedValue}
            sx={tabsSx}
          />

          <TabApp
            component={Link}
            to={makeSkillWebCompanyRedirectUrl(account.company.uniqueId)}
            label={t('navUserMenu.companyTitle')}
            value="monolith_manager_company"
            selectedValue={tabSelectedValue}
            sx={tabsSx}
          />

          <TabMenuApp
            label={t('navUserMenu.requestsTitle')}
            value="requests"
            selectedValue={tabSelectedValue}
            sx={tabsSx}
            options={[
              ...(enabledUpgradeV4Flag
                ? [
                    {
                      label: COMPANY_MANAGER_UPGRADE_ENROLLMENT.title,
                      to: COMPANY_MANAGER_UPGRADE_ENROLLMENT.value,
                      value: COMPANY_MANAGER_UPGRADE_ENROLLMENT.name
                    }
                  ]
                : []),
              {
                label: COMPANY_MANAGER_DEPENDENT_ENROLLMENT.title,
                to: COMPANY_MANAGER_DEPENDENT_ENROLLMENT.value,
                value: COMPANY_MANAGER_DEPENDENT_ENROLLMENT.name
              },
              {
                label: COMPANY_MANAGER_REQUESTS_CONFIGURATION.title,
                to: COMPANY_MANAGER_REQUESTS_CONFIGURATION.value,
                value: COMPANY_MANAGER_REQUESTS_CONFIGURATION.name
              }
            ]}
          />
        </Tabs>
      </Box>
    </Container>
  )
}
